import React from 'react';
import { 
  Card, 
  CardContent, 
  CardActions, 
  Typography, 
  Button, 
  Box, 
  useTheme, 
  useMediaQuery,
  IconButton
} from '@mui/material';
import { UcPackage } from '../../types';
import { colors } from '../../theme/themeConfig';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import UcIcon from '../../assets/icons/UcIcon';
import StarIcon from '@mui/icons-material/Star';

interface UcPackageCardProps {
  package: UcPackage;
  onBuy: (pkg: UcPackage) => void;
  onSelectCodes?: (pkg: UcPackage, count: number, shouldReset?: boolean) => void;
}

const UcPackageCard: React.FC<UcPackageCardProps> = ({ 
  package: pkg, 
  onBuy,
  onSelectCodes 
}) => {
  const theme = useTheme();
  // Use Material UI's breakpoint system
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Fixed numeric values for icon dimensions
  const iconSize = isMobile ? 24 : 30;
  
  const handleIncrement = () => {
    if (onSelectCodes && pkg.selectedCodes !== undefined) {
      const maxCodes = pkg.availableCodes || 0;
      if (pkg.selectedCodes < maxCodes) {
        onSelectCodes(pkg, pkg.selectedCodes + 1);
      }
    }
  };

  const handleDecrement = () => {
    if (onSelectCodes && pkg.selectedCodes !== undefined && pkg.selectedCodes > 0) {
      if (pkg.selectedCodes === 1) {
        // Reset to 0 (back to default button)
        onSelectCodes(pkg, 0, true);
      } else {
        onSelectCodes(pkg, pkg.selectedCodes - 1);
      }
    }
  };

  const isIncrementDisabled = () => {
    const maxCodes = pkg.availableCodes || 0;
    return (pkg.selectedCodes || 0) >= maxCodes;
  };

  return (
    <Card 
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.background.card,
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[2],
        border: `1px solid ${colors.primary.main}`,
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      }}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: theme.spacing(0.75),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              fontSize: theme.typography.pxToRem(22),
              fontWeight: 500,
              color: colors.text.primary,
              margin: 0,
              whiteSpace: 'nowrap',
            }}
          >
            {pkg.amount}
          </Typography>
          <Box sx={{ marginLeft: theme.spacing(0.5) }}>
            <UcIcon 
              width={iconSize} 
              height={iconSize} 
              color={colors.primary.main}
              className="uc-icon"
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography 
            component="span" 
            sx={{
              color: colors.secondary.main,
              fontWeight: 500,
              fontSize: theme.typography.pxToRem(14),
              marginRight: theme.spacing(0.25),
            }}
          >
            {pkg.bonusPoints}
          </Typography>
          <StarIcon 
            sx={{ 
              color: colors.secondary.main,
              fontSize: theme.typography.pxToRem(16)
            }} 
          />
        </Box>
      </Box>
      
      <CardContent 
        sx={{ 
          flex: 1,
          padding: 0,
          marginTop: theme.spacing(-0.5),
          marginBottom: theme.spacing(0.75),
          '&:last-child': { paddingBottom: 0 },
        }}
      >
        <Typography 
          variant="body1" 
          sx={{ 
            fontSize: theme.typography.pxToRem(14),
            fontWeight: 500,
            marginBottom: 0,
            color: colors.text.primary,
          }}
        >
          {pkg.price} рублей
        </Typography>
      </CardContent>
      
      <CardActions sx={{ padding: 0 }}>
        {pkg.selectedCodes === 0 || pkg.selectedCodes === undefined ? (
          <Button 
            variant="contained" 
            color="primary"
            fullWidth
            onClick={() => onSelectCodes ? onSelectCodes(pkg, 1) : onBuy(pkg)}
            disabled={!pkg.availableCodes}
            sx={{
              borderRadius: (theme.shape.borderRadius as number) / 2,
              padding: theme.spacing(1, 0),
              fontSize: theme.typography.pxToRem(14),
              fontWeight: 500,
              textTransform: 'none',
              transition: 'background-color 0.2s ease-in-out',
            }}
          >
            Выбрать
          </Button>
        ) : (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              width: '100%',
              border: `1px solid ${colors.primary.main}`,
              borderRadius: (theme.shape.borderRadius as number) / 2,
              padding: theme.spacing(0.6),
            }}
          >
            <IconButton 
              onClick={handleDecrement}
              size="small"
              sx={{ 
                color: colors.primary.main,
                transition: 'color 0.2s ease-in-out',
                '&:hover': {
                  color: colors.primary.dark,
                }
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            
            <Typography 
              sx={{ 
                fontSize: theme.typography.pxToRem(14),
                fontWeight: 500,
                color: colors.text.primary,
              }}
            >
              {pkg.selectedCodes}
            </Typography>
            
            <IconButton 
              onClick={handleIncrement}
              size="small"
              disabled={isIncrementDisabled()}
              sx={{ 
                color: isIncrementDisabled() ? colors.text.disabled : colors.primary.main,
                transition: 'color 0.2s ease-in-out',
                '&:hover': {
                  color: isIncrementDisabled() ? colors.text.disabled : colors.primary.dark,
                }
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </CardActions>
      
      <Typography 
        variant="caption" 
        align="center"
        sx={{
          fontSize: theme.typography.pxToRem(9),
          color: colors.text.secondary,
          marginTop: theme.spacing(0.5),
        }}
      >
        Вы получите бонусы при оплате через СБП
      </Typography>
    </Card>
  );
};

export default UcPackageCard;
